// SQL 04 · OLTP vs OLAP — graded practice bank.
//
// sql-04-oltp-olap-guide.md teaches why transactional and analytical systems
// are shaped differently — normalization vs denormalization, row-store vs
// columnstore, star vs snowflake, SCD, and ETL/ELT + distribution. These five
// cases walk that same territory on the same Northstar data, one decision
// per topic.
//
// Every table is real data from /data/datasets/table-detective-retail-export.csv
// and every figure is computed from it: 15 rows, 10 orders, 6 customers,
// 4 stores, 6 products, 1,985.75 total.

export const SQL04_OLTP_OLAP_TRACKER_ID = 'sql-04-oltp-olap-practice-bank';

export const SQL04_OLTP_OLAP_LENSES = [
  ['shape', 'OLTP or OLAP shape'],
  ['schema', 'Schema / structure'],
  ['rule', 'The rule or mechanism'],
  ['evidence', 'Evidence in the data'],
  ['tradeoff', 'Cost / trade-off'],
  ['caution', 'Risk if you get it wrong'],
];

export const SQL04_OLTP_OLAP_ITEMS = [
  {
    id: 'OL01',
    title: 'OL 01: The receipt printer shortcut',
    scenario: "A new engineer proposes adding customer_name and product_name directly onto OrderItems, \"so the receipt printer doesn't need to join three tables at checkout — it'll be faster.\" Decide whether to make this change to the live till schema.",
    tables: [{
      columns: ['order_line_id', 'order_id', 'product_id', 'product_name'],
      rows: [
        ['O-1001-1', 'O-1001', 'P-100', 'Wireless Mouse'],
        ['O-1003-1', 'O-1003', 'P-100', 'Wireless Mouse'],
        ['O-1007-1', 'O-1007', 'P-100', 'Wireless Mouse'],
      ],
    }],
    answerGuide: {
      shape: "This is an OLTP schema decision. The receipt printer is a live, per-transaction read at checkout, not an analytical rollup, so the question is normalization, not fact/dimension design.",
      schema: "Keep customer_name and product_name in Customers and Products only. OrderItems keeps customer_id and product_id as foreign keys, joined at read time.",
      rule: "Normalization (3NF) stores each fact exactly once; copying product_name onto every order line recreates the exact redundancy 3NF exists to prevent.",
      evidence: "Wireless Mouse (P-100) appears on three separate lines — O-1001-1, O-1003-1 and O-1007-1. Denormalizing would duplicate the name three times, and a rename would need three separate updates.",
      tradeoff: "The join at checkout costs a small amount of read latency but keeps a single, correctable source of truth; denormalizing shaves that join at the cost of update anomalies.",
      caution: "If Wireless Mouse is later renamed, every historical OrderItems row keeps the old name unless someone remembers to update every copy — a receipt-speed optimization has quietly created a data-integrity bug.",
    },
    ruleTerms: {
      shape: ['oltp', 'transaction', 'checkout'],
      schema: ['customers', 'products', 'foreign key'],
      rule: ['normalization', '3nf', 'exactly once'],
      evidence: ['p-100', 'o-1001-1', 'o-1003-1', 'o-1007-1'],
      tradeoff: ['join', 'latency', 'source of truth'],
      caution: ['rename', 'every copy', 'anomaly'],
    },
    criticalTerms: { shape: ['OLTP'], evidence: ['P-100'] },
  },
  {
    id: 'OL02',
    title: 'OL 02: One table, two very different reads',
    scenario: "OrderItems is used two ways: the checkout screen looks up all lines for a single order_id in milliseconds, and the monthly report scans the whole table to sum revenue by category. A consultant suggests storing OrderItems as a columnstore table everywhere, \"since analytics matters more.\" Decide the storage layout.",
    tables: [{
      columns: ['order_line_id', 'order_id', 'product_id', 'line_total'],
      rows: [
        ['O-1005-1', 'O-1005', 'P-214', '159.60'],
        ['O-1005-2', 'O-1005', 'P-700', '133.00'],
      ],
    }],
    answerGuide: {
      shape: "The checkout lookup is OLTP-shaped — a few rows for one order_id. The monthly rollup is OLAP-shaped — millions of rows, only a handful of columns. One physical table is being asked to serve two different jobs.",
      schema: "Keep the live OrderItems table row-store for the checkout path, and feed a separate FactSales table (a columnstore copy) for the monthly report, populated by a scheduled ETL/ELT load.",
      rule: "A row-store keeps all columns of one row together, which is fast for \"fetch order O-1005's lines\"; a columnstore keeps all values of one column together, which is fast for \"sum line_total across everything\" but slow for fetching one row's worth of columns.",
      evidence: "O-1005 needs its 2 rows in full detail for the checkout screen (159.60 and 133.00); the monthly report needs a SUM over all 15 rows' line_total, touching only one or two columns.",
      tradeoff: "Running two physical copies costs storage and pipeline complexity (an ETL job to keep them in sync), in exchange for each workload getting the storage layout it actually needs.",
      caution: "Converting the live OrderItems table to columnstore would slow every checkout, because point-lookups on a handful of rows are exactly what columnstore is bad at — \"analytics matters more\" doesn't mean the transactional path can be sacrificed to it.",
    },
    ruleTerms: {
      shape: ['oltp', 'olap', 'two jobs'],
      schema: ['row-store', 'columnstore', 'factsales'],
      rule: ['row together', 'column together', 'fetch'],
      evidence: ['o-1005', '159.60', '133.00'],
      tradeoff: ['storage', 'pipeline', 'sync'],
      caution: ['slow every checkout', 'point-lookup', 'sacrificed'],
    },
    criticalTerms: { rule: ['row-store', 'columnstore'], evidence: ['O-1005'] },
  },
  {
    id: 'OL03',
    title: 'OL 03: The hierarchy merchandising wants',
    scenario: "DimProduct currently holds product_category as a single flat text column. Merchandising now wants to track category -> sub_category -> department as a proper shared hierarchy across dozens of product lines, and wants renaming a department to update every product under it in one place. Decide the dimension's structure.",
    tables: [{
      columns: ['product_id', 'product_name', 'product_category'],
      rows: [
        ['P-100', 'Wireless Mouse', 'Accessories'],
        ['P-330', 'Office Chair', 'Furniture'],
        ['P-501', 'Monitor 24 inch', 'Electronics'],
      ],
    }],
    answerGuide: {
      shape: "This is an OLAP dimension-design decision — how much to normalize inside an otherwise denormalized star schema.",
      schema: "Split DimProduct into a snowflake: DimProduct(product_key, product_id, product_name, category_key FK) -> DimCategory(category_key, category_name, department_key FK) -> DimDepartment(department_key, department_name), instead of keeping category as flat text.",
      rule: "A snowflake schema normalizes a dimension into sub-dimensions, trading extra joins per query for consistency and single-point updates across a shared hierarchy.",
      evidence: "This export only has 3 categories — Furniture, Electronics, Accessories — among 6 products, small enough that flat text works today, but merchandising's ask (renaming a department once) is exactly the update-anomaly problem normalization solves as the hierarchy grows.",
      tradeoff: "Snowflaking adds a join per level when querying revenue by department, at the cost of query simplicity, in exchange for renaming a category being one UPDATE instead of many.",
      caution: "Snowflaking every dimension \"for consistency\" defeats the purpose of a star schema, which is to minimize joins for analysts — reserve it for dimensions with a real, shared, deep hierarchy like this one, not for small flat lookups.",
    },
    ruleTerms: {
      shape: ['olap', 'dimension', 'star'],
      schema: ['snowflake', 'dimcategory', 'dimdepartment'],
      rule: ['normalizes', 'sub-dimension', 'joins'],
      evidence: ['3', 'furniture', 'electronics', 'accessories'],
      tradeoff: ['join per level', 'one update'],
      caution: ['every dimension', 'defeats the purpose', 'flat lookups'],
    },
    criticalTerms: { schema: ['snowflake'], evidence: ['3'] },
  },
  {
    id: 'OL04',
    title: 'OL 04: The reclassified office chair',
    scenario: "Merchandising reclassifies the Office Chair (P-330) from Furniture to Office Equipment. They want reports to show \"revenue under the old category\" and \"revenue under the new category\" side by side for exactly this one transition, without keeping a full version history of every past change. Decide the history-handling approach.",
    tables: [{
      columns: ['order_id', 'order_line_id', 'product_id', 'line_total'],
      rows: [
        ['O-1002', 'O-1002-1', 'P-330', '102.00'],
        ['O-1007', 'O-1007-2', 'P-330', '120.00'],
      ],
    }],
    answerGuide: {
      shape: "OLAP dimension history decision, inside DimProduct's Slowly Changing Dimension handling.",
      schema: "Add current_category and previous_category columns to DimProduct (SCD Type 3), rather than inserting a new versioned row (Type 2) or overwriting in place (Type 1).",
      rule: "Type 3 adds a column to hold the prior value, giving exactly \"current vs one prior state\" — it does not preserve a full timeline of every change the way Type 2 does.",
      evidence: "Office Chair P-330 generated 102.00 (O-1002) plus 120.00 (O-1007-2), 222.00 total, under Furniture before the reclassification. Type 3 lets a report show that 222.00 under previous_category = Furniture, while all future P-330 sales roll up under current_category = Office Equipment.",
      tradeoff: "Type 3 costs almost nothing extra — two columns, no extra rows — and answers \"before vs after\" cleanly, but it cannot answer \"what was the category as of last March\" if there is ever a second reclassification, since that second change overwrites the previous_category column.",
      caution: "If merchandising ever needs a third state, or a real point-in-time history query, Type 3 breaks silently by overwriting previous_category again rather than raising an error — confirm this really is a one-time, two-state transition before choosing Type 3 over Type 2.",
    },
    ruleTerms: {
      shape: ['olap', 'dimension', 'scd'],
      schema: ['current_category', 'previous_category', 'type 3'],
      rule: ['prior value', 'current vs one prior', 'not type 2'],
      evidence: ['222.00', 'p-330', 'office chair'],
      tradeoff: ['before vs after', 'point-in-time', 'overwrites'],
      caution: ['third state', 'silently', 'one-time'],
    },
    criticalTerms: { rule: ['Type 3'], evidence: ['222.00'] },
  },
  {
    id: 'OL05',
    title: 'OL 05: Choosing a distribution column in Synapse',
    scenario: "FactSales is being built in an Azure Synapse dedicated SQL pool. An engineer proposes hash-distributing FactSales on customer_id, \"since that's the most common join.\" Repeat customers like Dina Shah (C-115) and Amara Okafor (C-042) each appear on multiple orders in this export. Decide the distribution strategy.",
    tables: [{
      columns: ['customer_id', 'customer_name', 'orders'],
      rows: [
        ['C-115', 'Dina Shah', 'O-1005, O-1009'],
        ['C-042', 'Amara Okafor', 'O-1001, O-1004'],
      ],
    }],
    answerGuide: {
      shape: "This is a Synapse/OLAP physical-design decision on the fact table's distribution column, downstream of the ETL/ELT load from the OLTP source.",
      schema: "Hash-distribute FactSales on product_key (or another high-cardinality, evenly-spread column), not customer_id; replicate small dimensions like DimStore across all nodes.",
      rule: "Hash distribution spreads rows across 60 distributions by a hash of one column; picking a low-cardinality or skewed column concentrates rows on a few distributions, causing data-movement (shuffle) that dominates query time in an MPP engine.",
      evidence: "Accessories products (P-100, P-214, P-700) already cover 8 of the 15 order lines in this sample, and repeat customers like Dina Shah (C-115) and Amara Okafor (C-042) each appear on multiple orders — distributing on customer_id at full scale would concentrate a disproportionate share of rows on whichever distributions hold Northstar's highest-volume customers.",
      tradeoff: "product_key gives a more even spread across distributions at the cost of not being the join column for every query, so some queries still pay a data-movement cost — no single distribution column is free for every query pattern.",
      caution: "Choosing a distribution column purely because \"that's the most common join\" ignores cardinality and skew entirely — this is the #1 cause of slow dedicated-SQL-pool queries, showing up as shuffle steps in the query plan that stay invisible until the table is full-sized.",
    },
    ruleTerms: {
      shape: ['synapse', 'mpp', 'olap'],
      schema: ['hash distribution', 'product_key', 'replicate'],
      rule: ['60 distributions', 'skewed', 'data movement'],
      evidence: ['c-115', 'c-042', '8 of the 15'],
      tradeoff: ['even spread', 'not the join column'],
      caution: ['most common join', 'cardinality', 'shuffle'],
    },
    criticalTerms: { rule: ['hash distribution'], evidence: ['C-115'] },
  },
];

export const SQL04_OLTP_OLAP_CONFIG = {
  trackerId: SQL04_OLTP_OLAP_TRACKER_ID,
  resourceKey: 'sql_04_oltp_olap',
  rubricKey: 'oltp_olap_shape_and_platform',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: OLTP or OLAP, on Real Data',
  description: 'One case per decision — normalization, storage layout, dimension structure, history, distribution — each a real request with the answer deliberately not named in the title. Your work autosaves to your own practice record.',
  introAlert: 'Name the shape (OLTP or OLAP) before you propose a structure. A schema chosen before the workload is identified is a guess, and it will be marked as one.',
  lenses: SQL04_OLTP_OLAP_LENSES,
  workingField: {
    key: 'shape_sketch',
    label: 'Shape / structure sketch',
    placeholder: 'Example: FactSales(date_key FK, customer_key FK, product_key FK, store_key FK, quantity, revenue) DISTRIBUTION = HASH(product_key) — name the shape, then sketch the structure.',
    prompts: SQL04_OLTP_OLAP_ITEMS.reduce((acc, item) => ({
      ...acc,
      [item.id]: `Sketch the structure this case needs for ${item.title}. Name the shape (OLTP or OLAP) first, then sketch the schema or DDL.`,
    }), {}),
  },
  items: SQL04_OLTP_OLAP_ITEMS,
};
